'use client'

import { GoogleAnalytics, GoogleTagManager, sendGAEvent, sendGTMEvent } from '@next/third-parties/google'
import { Box } from '@web/components/atoms/Layout'
import { ActionDialog } from '@web/components/molecules/ActionDialog'
import { useMergeFields } from '@web/components/page-components/Text/MergeTags/MergeTag'
import { CAMPAIGN_MERGE_TAGS, CHARITY_MERGE_TAGS } from '@web/components/page-components/Text/MergeTags/utils'
import { Nullable } from '@web/components/page-components/utils/types'
import { Sections, useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import { DonationForm, DonationFrequency } from '@web/graphql-components'
import {
  type Page as PageLayoutType,
  ComponentType,
  DonationPageLayoutFragment,
  PageMetadataQuery,
  PaymentSettingsQuery,
  ProfileDataQuery,
  PurlQuery,
  RecordStatsMutation,
  SearchBySlugQuery,
} from '@web/graphql-queries/graphql'
import AuthValidationHandler from 'apps/fe-donation/components/AuthValidationHandler'
import FacebookPixel from 'apps/fe-donation/components/FacebookPixel'
import FieldCollectionPopup from 'apps/fe-donation/components/FieldCollectionPopup'
import MatchedGivingHandler from 'apps/fe-donation/components/MatchedGivingHandler'
import PageLayout from 'apps/fe-donation/components/PageLayout'
import TestModeBanner from 'apps/fe-donation/components/TestModeBanner'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { usePaymentStore } from 'apps/fe-donation/libs/hooks/usePaymentStore'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import { v4 } from 'uuid'
import * as pixel from '../../libs/fpixel'
import useGraphQLClient from 'apps/fe-donation/libs/hooks/useGraphQLClient'
import { recordStatsDocument } from 'apps/fe-donation/libs/queries'
import { getAnalytics } from 'apps/fe-donation/utils/analytics'
import dynamic from 'next/dynamic'
import environment from 'apps/fe-donation/libs/environment'
const PayPalScriptProvider = dynamic(() => import('@paypal/react-paypal-js').then((mod) => mod.PayPalScriptProvider), {
  ssr: false,
})

type PageClientProps = {
  charityId: string
  campaignId: string
  pageId: Nullable<string>
  pageBucketId: Nullable<string>
  layout: DonationPageLayoutFragment
  paymentSettings: Nullable<PaymentSettingsQuery['paymentSettings']>
  isLiveMode: boolean
  purl: PurlQuery | null | undefined
  profileId: Nullable<string>
  profileData: Nullable<ProfileDataQuery['profileData']>
  campaignSettings: SearchBySlugQuery['searchBySlug']['campaignSettings'] | null | undefined
  metadata: PageMetadataQuery['pageMetadata']
}

const PayPalScriptProviderWrapper = ({
  paymentSettings,
  children,
}: {
  paymentSettings: Nullable<PaymentSettingsQuery['paymentSettings']>
  children: React.ReactNode
}) => {
  const paypalSettings = paymentSettings?.donationPagePaymentProviderSettings.payPalMerchantSetting
  if (!paypalSettings || !paypalSettings.merchantEmail || !paypalSettings.merchantId) {
    return <>{children}</>
  }

  return (
    <PayPalScriptProvider
      options={{
        clientId: paypalSettings.clientId ?? environment.payPalClientId,
        merchantId: paypalSettings.merchantId,
        currency: 'AUD',
        disableFunding: 'credit',
        components: ['buttons'],
      }}
      deferLoading={true}
    >
      {children}
    </PayPalScriptProvider>
  )
}

const getBrandingValuesFromString = (value: Nullable<string>) => {
  try {
    if (!value) {
      return null
    }

    const branding = JSON.parse(value)

    return {
      name: branding.name ?? '',
      logoData: branding[Sections.Logo] ?? {},
      fontData: branding[Sections.Fonts],
      colorData: branding[Sections.Colors],
      buttonData: branding[Sections.Buttons],
    }
  } catch (e) {
    return null
  }
}

const [CharityName, CharityABN, CharityPhone, CharityEmail] = CHARITY_MERGE_TAGS
const [CampaignName] = CAMPAIGN_MERGE_TAGS

const PageClient = ({
  layout,
  metadata,
  purl,
  charityId,
  campaignId,
  pageId,
  pageBucketId,
  isLiveMode,
  profileId,
  profileData,
  campaignSettings,
  paymentSettings,
}: PageClientProps) => {
  const path = usePathname()
  const searchParams = useSearchParams()
  const isThankYouPage = usePageStore((state) => state.isThankYouPage)
  const thankYouPageLayout = usePageStore((state) => state.thankYouPageLayout)
  const layoutToUse = (isThankYouPage ? thankYouPageLayout || layout : layout) as PageLayoutType
  const isThanks = path?.endsWith('/thanks') ?? false

  // Initialise page branding store
  const pageBranding = useMemo(() => {
    if (!layoutToUse?.branding) {
      return getBrandingValuesFromString(layout.branding)
    }

    return getBrandingValuesFromString(layoutToUse.branding)
  }, [layoutToUse])

  useEffect(() => {
    useBrandingsStore.getState().actions.loadBranding(pageBranding)
  }, [pageBranding])

  // initial page stores
  useEffect(() => {
    const charityDetails: [string, string][] = metadata?.charity
      ? [
          [CharityName.MergeFieldSettingId, metadata.charity.CharityName ?? ''],
          [CharityABN.MergeFieldSettingId, metadata.charity.ABN ?? ''],
          [CharityPhone.MergeFieldSettingId, metadata.charity.Phone ?? ''],
          [CharityEmail.MergeFieldSettingId, metadata.charity.Email ?? ''],
        ]
      : []
    const campaignDetails: [string, string][] = metadata?.campaign
      ? [[CampaignName.MergeFieldSettingId, metadata.campaign.CampaignName ?? '']]
      : []

    usePageStore.setState((state) => ({
      ...state,
      charityId,
      campaignId,
      pageId,
      pageBucketId,
      isLiveMode,
      profileId,
      currentUrl: state.currentUrl ?? window?.location.href,
      elementSettings: {
        progressBar: {
          pageIds: layout?.elementSettings?.progressBar?.pageIds ?? [pageId],
          goals: layout?.elementSettings?.progressBar?.progressBarGoals?.map((g) => g.goal) ?? [],
        },
      },
      profileData,
      campaignDomain: campaignSettings?.campaignDomain,
      isPeerToPeerGivingNotificationEnabled: campaignSettings?.isPeerToPeerGivingNotificationEnabled,
    }))
    usePaymentStore.setState((state) => ({ ...state, paymentSettings, hasLoaded: true }))
    useMergeFields.setState((state) => ({
      isEditing: false,
      mergeFields: new Map<string, string>([
        ['Giving.Amount', searchParams.get('amount') || state.mergeFields?.get('Giving.Amount') || ''],
        ...(state.mergeFields ? Array.from(state.mergeFields) : []),
        ...(purl ? Object.entries(purl) : []),
        ...(searchParams?.entries() ?? []),
        ...charityDetails,
        ...campaignDetails,
      ]),
    }))

    if (isThanks) {
      const paymentTransactionId = searchParams.get('ptid') ?? undefined
      const paymentSubscriptionId = searchParams.get('psid') ?? undefined

      if (!paymentTransactionId || !paymentSubscriptionId) {
        return
      }

      usePaymentStore.setState((state) => ({
        ...state,
        paymentTransactionId,
        paymentSubscriptionId,
        showDataCollectionPopup: true,
      }))
    }
  }, [])

  const shouldShowPopup = usePaymentStore((state) => state.showDataCollectionPopup)

  // send analytics events on donate
  const isFbPixelLoaded = usePageStore((state) => state.isFbPixelLoaded)
  const pageEventId = useMemo(() => v4(), [])
  const graphQLClient = useGraphQLClient()

  useEffect(() => {
    const { amount, purchaseEventId, donationFrequency, customFieldValues, isThankYouPage } = usePageStore.getState()

    if (isThankYouPage && !isThanks) {
      usePageStore.setState({ isThankYouPage: false, amount: 0 })
      usePaymentStore.setState({
        prevAmount: 0,
        prevEmail: '',
        prevGivingFrequency: DonationFrequency.OneTime,
        paymentTransactionId: '',
        paymentSubscriptionId: '',
        paymentIntentId: '',
        paymentMethodType: null,
        subscriptionId: null,
        showDataCollectionPopup: false,
        prevIsDonorPays: false,
      })

      return
    }

    const { paymentTransactionId, paymentSubscriptionId } = usePaymentStore.getState()

    if (!amount || !isThanks || (!paymentTransactionId && !paymentSubscriptionId)) {
      return
    }

    if (purchaseEventId && window.fbq) {
      pixel.event(
        donationFrequency === DonationFrequency.OneTime ? 'Purchase' : 'Subscribe',
        { currency: 'AUD', value: amount },
        { eventID: purchaseEventId },
      )
      pixel.event('Donate', {}, { eventID: purchaseEventId })
    }

    if (!path) {
      return
    }

    const pathSplit = path.split('/').filter((x) => x && !['thanks', 'receipt'].includes(x.toLowerCase()))
    if (pathSplit && pathSplit.length > 2) {
      pathSplit.splice(0, 1) // for guid paths to remove charity id
    }

    const normalisedCampaignName = metadata?.campaign?.CampaignName?.toLowerCase()?.replace(/\s/g, '-') ?? ''
    const itemId = pathSplit.length > 0 ? pathSplit.join('/') : normalisedCampaignName
    const itemName = pathSplit.length > 0 ? (pathSplit.at(-1) ?? normalisedCampaignName) : normalisedCampaignName

    console.log('Sending analytics event with itemId:', itemId, 'and itemName:', itemName)
    console.log('itemId - pathsplit:', pathSplit.join('/'), ' normalizedCampaignName:', normalisedCampaignName)
    console.log('itemName - pathsplit:', pathSplit.at(-1), ' normalizedCampaignName:', normalisedCampaignName)

    if (metadata?.campaign?.gtmScriptId) {
      sendGTMEvent({
        event: 'purchase',
        value: {
          ...customFieldValues,
          transaction_id: paymentTransactionId,
          value: amount,
          currency: 'AUD',
          items: [
            {
              item_id: itemId,
              item_name: itemName,
              price: amount,
              quantity: 1,
            },
          ],
        },
      })
    }
    if (metadata?.campaign?.ga4ScriptId) {
      sendGAEvent('event', 'purchase', {
        ...customFieldValues,
        transaction_id: paymentTransactionId,
        value: amount,
        currency: 'AUD',
        items: [
          {
            item_id: itemId,
            item_name: itemName,
            price: amount,
            quantity: 1,
          },
        ],
      })
    }
  }, [path])

  useEffect(() => {
    if (!layout || (metadata?.campaign?.fbPixelId && !isFbPixelLoaded)) {
      return
    }

    const pollStats = (retryCount = 0) => {
      setTimeout(async () => {
        if (metadata?.campaign?.fbPixelId && window._fbq.queue.length && retryCount < 6) {
          pollStats(retryCount + 1)

          return
        }

        try {
          if (!metadata?.campaign) {
            return
          }

          await graphQLClient<RecordStatsMutation>({
            document: recordStatsDocument,
            options: {
              input: getAnalytics(
                pageEventId,
                metadata.campaign.fbPixelId ?? '',
                metadata.campaign.isConversionsApi ?? false,
              ),
            },
          })
        } catch (e) {
          // handleError(e)
        }
      }, 500)
    }

    pollStats()
  }, [isFbPixelLoaded])

  useEffect(() => {
    if (!isFbPixelLoaded) {
      return
    }

    pixel.pageview()
    pixel.event('ViewContent', {}, { eventID: pageEventId })
  }, [isFbPixelLoaded, layoutToUse])

  return (
    <>
      {metadata?.campaign?.gtmScriptId && <GoogleTagManager gtmId={metadata.campaign.gtmScriptId} />}
      {metadata?.campaign?.ga4ScriptId && <GoogleAnalytics gaId={metadata.campaign.ga4ScriptId} />}
      {metadata?.campaign?.fbPixelId && <FacebookPixel pixelId={metadata.campaign.fbPixelId} />}
      {isThanks && shouldShowPopup && (
        <FieldCollectionPopup
          donationForm={layout.components.find((i) => i.type === ComponentType.DonationForm) as DonationForm}
          branding={pageBranding}
        />
      )}
      {!isLiveMode && <TestModeBanner />}
      <PayPalScriptProviderWrapper paymentSettings={paymentSettings}>
        <Box w="100%" paddingTop={isLiveMode ? '0' : '36px'}>
          <PageLayout key={`page-${layoutToUse.id}`} branding={pageBranding} layout={layoutToUse} />
        </Box>
      </PayPalScriptProviderWrapper>
      <ActionDialog />
      <AuthValidationHandler charityId={charityId} profileId={profileId} campaignId={campaignId} />
      <MatchedGivingHandler matchedGivings={layoutToUse?.elementSettings?.matchedGivings} />
    </>
  )
}

export default dynamic(() => Promise.resolve(PageClient), { ssr: false })
